// src/pages/Payments/PaymentList.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import Pager from '../../components/common/Pager';
import { PlusIcon } from '@heroicons/react/24/outline';
import { usePaymentsPage } from '../../hooks/usePaymentsPage';

const PaymentList = () => {
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Debounce the search term to avoid refetching on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Pagination (server-side)
  const [pageSize, setPageSize] = useState(20); // default 20
  const [currentPage, setCurrentPage] = useState(1);

  // Server-side data (rows + count come from DB)
  const {
    rows,
    count,
    isLoading,
    error,
    softDeletePayment,
  } = usePaymentsPage({
    page: currentPage,
    pageSize,
    search: debouncedSearch, // use debounced term
    status: statusFilter,
  });

  // Reset to first page when the *debounced* search/status/pageSize changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, pageSize]);

  // Clamp page if total shrinks (e.g., after delete or filters)
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((count || 0) / (pageSize || 1)));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [count, pageSize, currentPage]);

  const handleDeletePayment = async (id, student_id) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    try {
      await softDeletePayment({ id }); // student_id not required here
      alert('Payment deleted successfully');
      // Page clamping handled by effect above (after count refresh)
    } catch (e) {
      alert(`Error deleting payment: ${e?.message || e}`);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Refunded':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentTypeBadgeClass = (type) =>
    type === 'Full' ? 'bg-purple-100 text-purple-800' : 'bg-indigo-100 text-indigo-800';

  const title = useMemo(() => 'Payments Management', []);
  const isTyping = searchTerm.trim() !== debouncedSearch; // indicates a pending debounce window

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-xl font-semibold">{title}</h1>
        <Link to="/payments/new" className="sm:ml-auto">
          <Button>
            <PlusIcon className="h-5 w-5 mr-1" />
            Add New Payment
          </Button>
        </Link>
      </div>

      {/* Error banner (non-blocking; we never unmount filters) */}
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
          {error}
        </div>
      )}

      {/* Filters (stay mounted; no early returns) */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              {/* Tiny non-disruptive status hint */}
              <span className="text-xs text-gray-500">
                {isTyping ? 'Typing…' : isLoading ? 'Searching…' : ''}
              </span>
            </div>
            <input
              type="text"
              id="search"
              placeholder="Search by Invoice #, Student Name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Paid">Paid</option>
              <option value="Refunded">Refunded</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {count === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {isLoading && !isTyping
              ? 'Loading payments…'
              : `No payments${debouncedSearch || statusFilter !== 'All' ? ' match your filters' : ' found'}`}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto relative">
              {/* Subtle top border loader that does not unmount content */}
              {isLoading && !isTyping && (
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent animate-pulse" />
              )}
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{p.invoice_number}</div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{p.student_name || 'N/A'}</div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          RM{Number(p.amount ?? 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {p.payment_date
                            ? new Date(p.payment_date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })
                            : 'N/A'}
                        </div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{p.payment_method}</div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentTypeBadgeClass(p.payment_type)}`}>
                          {p.payment_type}
                        </span>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(p.status)}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2 justify-end">
                          <Link to={`/payments/${p.id}`}>
                            <Button variant="outline" size="sm">View</Button>
                          </Link>
                          <Link to={`/payments/${p.id}/edit`}>
                            <Button variant="outline" size="sm">Edit</Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeletePayment(p.id, p.student_id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer — shared Pager (uses DB `count`) */}
            <Pager
              pageSize={pageSize}
              setPageSize={setPageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalRows={count}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentList;
